<template>
  <div class="cc-wrap">
    <div class="cc-header">
      <button class="cc-nav" @click="prev">‹</button>
      <span class="cc-title">{{ mode === 'year' ? viewY : monthLabel }}</span>
      <button class="cc-nav" @click="next">›</button>
      <div class="cc-legend">
        <span class="cc-leg"><span class="cc-dot" style="background:#16a34a"></span>Aprobat</span>
        <span class="cc-leg"><span class="cc-dot" style="background:#ea580c"></span>În așteptare</span>
        <span class="cc-leg"><span class="cc-dot" style="background:#e11d48"></span>Liber oficial</span>
      </div>
      <button class="cc-today" @click="goToday">Azi</button>
    </div>

    <div v-if="mode !== 'year'" class="cc-grid">
      <div v-for="d in dows" :key="d" class="cc-dow">{{ d }}</div>
      <div
        v-for="(cell, i) in cells"
        :key="i"
        class="cc-cell"
        :class="{ 'cc-out': !cell.inMonth, 'cc-today': cell.today, 'cc-weekend': cell.weekend, 'cc-holiday': cell.holiday }"
      >
        <div class="cc-num">
          {{ cell.day }}
          <v-tooltip v-if="cell.holiday" location="top" :text="cell.holidayNume">
            <template #activator="{ props }"><span v-bind="props" class="cc-hol-dot">●</span></template>
          </v-tooltip>
        </div>
        <div class="cc-badges">
          <v-tooltip v-for="(b, bi) in cell.badges.slice(0, maxBadges)" :key="bi" location="top">
            <template #activator="{ props }">
              <span v-bind="props" class="cc-badge" :style="{ background: b.color }" @click="openDetalii(b)">{{ b.initiale }}</span>
            </template>
            <span>{{ b.nume }} — {{ b.tip }}</span>
          </v-tooltip>
          <v-menu v-if="cell.badges.length > maxBadges" location="top" :close-on-content-click="false">
            <template #activator="{ props }">
              <span v-bind="props" class="cc-more">+{{ cell.badges.length - maxBadges }}</span>
            </template>
            <v-card class="pa-2" max-width="280">
              <div v-for="(b, bi) in cell.badges" :key="bi" class="cc-more-item" @click="openDetalii(b)">
                <span class="cc-badge cc-badge-sm" :style="{ background: b.color }">{{ b.initiale }}</span>
                <span class="cc-more-name">{{ b.nume }}</span>
                <span class="cc-more-tip">{{ b.tip }}</span>
              </div>
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Vedere pe an: 12 luni mini -->
    <div v-else class="cc-year">
      <div class="cc-mini" v-for="mo in luniAn" :key="mo.luna">
        <div class="cc-mini-title">{{ mo.nume }}</div>
        <div class="cc-mini-grid">
          <div v-for="d in dows" :key="'d'+d" class="cc-mini-dow">{{ d }}</div>
          <template v-for="(cell, i) in mo.cells" :key="i">
            <v-tooltip v-if="cell.inMonth && (cell.badges.length || cell.holiday)" location="top">
              <template #activator="{ props }">
                <span v-bind="props" class="cc-mini-cell"
                      :class="{ hol: cell.holiday && !cell.badges.length, today: cell.today }"
                      :style="cell.badges.length ? { background: cell.badges[0].color, color: '#fff', borderColor: cell.badges[0].color } : {}"
                      @click="cell.badges.length ? openDetalii(cell.badges[0]) : null">{{ cell.day }}</span>
              </template>
              <div>
                <div v-if="cell.holiday">● {{ cell.holidayNume }}</div>
                <div v-for="(b, bi) in cell.badges" :key="bi">{{ b.nume }} — {{ b.tip }}</div>
              </div>
            </v-tooltip>
            <span v-else class="cc-mini-cell" :class="{ out: !cell.inMonth, today: cell.today }">{{ cell.inMonth ? cell.day : '' }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Popup detalii concediu -->
    <v-dialog v-model="dialogDetalii" max-width="420">
      <v-card v-if="sel" rounded="xl" class="cc-dlg">
        <div class="cc-dlg-head" :style="{ background: sel.color }">
          <div class="cc-dlg-ini">{{ sel.initiale }}</div>
          <div>
            <div class="cc-dlg-nume">{{ sel.nume }}</div>
            <div class="cc-dlg-status">{{ sel.stare === '1' ? '✓ Aprobat' : '⏳ În așteptare' }}</div>
          </div>
        </div>
        <div class="cc-dlg-body">
          <div class="cc-row"><span class="cc-lbl">Tip concediu</span><span class="cc-val">{{ sel.tip }}</span></div>
          <div class="cc-row"><span class="cc-lbl">De la</span><span class="cc-val">{{ fmt(sel.start) }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Până la</span><span class="cc-val">{{ fmt(sel.end) }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Durată</span><span class="cc-val">{{ nrZile(sel) }} {{ nrZile(sel) === 1 ? 'zi' : 'zile' }}</span></div>
        </div>
        <div class="cc-dlg-foot">
          <v-btn variant="tonal" color="grey-darken-1" @click="dialogDetalii = false">Închide</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

interface CalItem { nume: string; initiale: string; tip: string; stare: string; start: Date; end: Date }

const LUNI = ['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie', 'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie']

export default defineComponent({
  name: 'CalendarConcedii',
  props: {
    items: { type: Array as PropType<CalItem[]>, default: () => [] },
    holidays: { type: Array as PropType<any[]>, default: () => [] },
    initialDate: { type: Date, default: () => new Date() },
    maxBadges: { type: Number, default: 6 },
    mode: { type: String, default: 'month' }, // 'month' | 'year'
  },
  data() {
    const d = this.initialDate || new Date()
    return { viewY: d.getFullYear(), viewM: d.getMonth(), dialogDetalii: false, sel: null as any }
  },
  watch: {
    initialDate(nou: Date) {
      if (nou) { this.viewY = nou.getFullYear(); this.viewM = nou.getMonth() }
    },
  },
  computed: {
    dows(): string[] { return ['L', 'M', 'M', 'J', 'V', 'S', 'D'] },
    monthLabel(): string { return `${LUNI[this.viewM]} ${this.viewY}` },
    cells(): any[] { return this.buildCells(this.viewY, this.viewM) },
    luniAn(): any[] {
      return Array.from({ length: 12 }, (_, m) => ({ nume: LUNI[m], luna: m, cells: this.buildCells(this.viewY, m) }))
    },
  },
  methods: {
    stripTime(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x },
    holidayMap(): Record<string, string> {
      const hmap: Record<string, string> = {}
      for (const h of this.holidays) {
        const k = (h.Data || h.data || '').toString().slice(0, 10)
        if (k) hmap[k] = h.Nume || h.nume || 'Liber oficial'
      }
      return hmap
    },
    buildCells(y: number, m: number): any[] {
      const primaZi = new Date(y, m, 1)
      const offset = (primaZi.getDay() + 6) % 7 // luni = 0
      const start = new Date(y, m, 1 - offset)
      const azi = new Date(); azi.setHours(0, 0, 0, 0)
      const hmap = this.holidayMap()
      const out: any[] = []
      for (let i = 0; i < 42; i++) {
        const zi = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
        const cheieZi = `${zi.getFullYear()}-${String(zi.getMonth() + 1).padStart(2, '0')}-${String(zi.getDate()).padStart(2, '0')}`
        const badges = this.items
          .filter(it => zi >= this.stripTime(it.start) && zi <= this.stripTime(it.end))
          .map(it => ({
            initiale: it.initiale, nume: it.nume, tip: it.tip, stare: it.stare,
            start: it.start, end: it.end, color: this.culoare(it),
          }))
          .sort((a: any, b: any) => a.color === b.color ? 0 : (a.color === '#16a34a' ? -1 : 1))
        out.push({
          day: zi.getDate(),
          inMonth: zi.getMonth() === m,
          today: zi.getTime() === azi.getTime(),
          weekend: zi.getDay() === 0 || zi.getDay() === 6,
          holiday: !!hmap[cheieZi],
          holidayNume: hmap[cheieZi] || '',
          badges,
        })
      }
      return out
    },
    prev() {
      if (this.mode === 'year') { this.viewY-- ; return }
      if (this.viewM === 0) { this.viewM = 11; this.viewY-- } else { this.viewM-- }
    },
    next() {
      if (this.mode === 'year') { this.viewY++ ; return }
      if (this.viewM === 11) { this.viewM = 0; this.viewY++ } else { this.viewM++ }
    },
    goToday() { const d = new Date(); this.viewY = d.getFullYear(); this.viewM = d.getMonth() },
    culoare(it: CalItem): string {
      // Culoarea = statusul cererii: aprobat (verde) vs în așteptare (portocaliu).
      return it.stare === '1' ? '#16a34a' : '#ea580c'
    },
    openDetalii(b: any) { this.sel = b; this.dialogDetalii = true },
    fmt(d: Date): string {
      if (!d) return '—'
      const x = new Date(d)
      return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`
    },
    nrZile(b: any): number {
      if (!b?.start || !b?.end) return 0
      const a = this.stripTime(b.start), z = this.stripTime(b.end)
      return Math.round((z.getTime() - a.getTime()) / 86400000) + 1
    },
  },
})
</script>

<style scoped>
.cc-wrap { padding: 8px 12px 12px; }
.cc-header { display: flex; align-items: center; gap: 10px; padding: 4px 0 10px; }
.cc-title { font-weight: 700; color: #1e293b; text-transform: capitalize; min-width: 150px; }
.cc-nav { border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; width: 30px; height: 30px; cursor: pointer; font-size: 18px; line-height: 1; color: #475569; }
.cc-nav:hover { background: #f8f9fc; }
.cc-today { margin-left: auto; border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; padding: 4px 12px; cursor: pointer; font-size: 12px; font-weight: 600; color: #475569; }
.cc-today:hover { background: #f8f9fc; }
.cc-legend { display: flex; gap: 14px; margin-left: 16px; }
.cc-leg { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #64748b; font-weight: 600; }
.cc-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.cc-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
.cc-dow { text-align: center; font-size: 11px; font-weight: 700; color: #94a3b8; padding: 4px 0; }
.cc-cell { min-height: 92px; border: 1px solid #f1f5f9; border-radius: 8px; padding: 4px; background: #fff; overflow: hidden; }
.cc-out { background: #fafbfc; }
.cc-out .cc-num { color: #cbd5e1; }
.cc-weekend { background: #fcfcfd; }
.cc-holiday { background: #fff1f3; border-color: #fecdd3; }
.cc-hol-dot { color: #e11d48; font-size: 8px; margin-left: 4px; vertical-align: middle; cursor: default; }

/* Vedere pe an — compactă (6 luni/rând) */
.cc-year { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
@media (max-width: 1300px) { .cc-year { grid-template-columns: repeat(4, 1fr); } }
@media (max-width: 900px) { .cc-year { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 640px) { .cc-year { grid-template-columns: repeat(2, 1fr); } }
.cc-mini { border: 1px solid #eef1f6; border-radius: 8px; padding: 6px 5px; }
.cc-mini-title { text-align: center; font-weight: 700; font-size: 0.72rem; color: #334155; margin-bottom: 4px; text-transform: capitalize; }
.cc-mini-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; }
.cc-mini-dow { text-align: center; font-size: 8px; font-weight: 700; color: #cbd5e1; }
.cc-mini-cell {
  aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  font-size: 8.5px; color: #64748b; border-radius: 4px; border: 1px solid transparent; cursor: default;
}
.cc-mini-cell.out { color: transparent; }
.cc-mini-cell.hol { background: #fff1f3; color: #e11d48; font-weight: 700; }
.cc-mini-cell.today { outline: 1.5px solid #2563eb; }
.cc-mini-cell[style] { cursor: pointer; font-weight: 700; }
.cc-today { border: 2px solid #2563eb; }
.cc-num { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 3px; }
.cc-badges { display: flex; flex-wrap: wrap; gap: 3px; }
.cc-badge { color: #fff; font-size: 10px; font-weight: 700; padding: 2px 5px; border-radius: 5px; line-height: 1.3; cursor: pointer; letter-spacing: 0.3px; transition: transform .1s; }
.cc-badge:hover { transform: scale(1.08); }
.cc-badge-sm { margin-right: 6px; }
.cc-more { font-size: 10px; font-weight: 700; color: #64748b; background: #eef2f7; border-radius: 5px; padding: 2px 5px; cursor: pointer; }
.cc-more-item { display: flex; align-items: center; padding: 4px 2px; font-size: 12px; cursor: pointer; border-radius: 6px; }
.cc-more-item:hover { background: #f8f9fc; }
.cc-more-name { color: #334155; }
.cc-more-tip { margin-left: auto; color: #94a3b8; font-size: 11px; }

/* Popup detalii */
.cc-dlg { overflow: hidden; }
.cc-dlg-head { display: flex; align-items: center; gap: 14px; padding: 18px 20px; color: #fff; }
.cc-dlg-ini { width: 46px; height: 46px; border-radius: 12px; background: rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; letter-spacing: 0.5px; }
.cc-dlg-nume { font-size: 16px; font-weight: 700; line-height: 1.2; }
.cc-dlg-status { font-size: 12px; opacity: 0.95; margin-top: 2px; }
.cc-dlg-body { padding: 16px 20px; }
.cc-row { display: flex; justify-content: space-between; padding: 9px 0; border-bottom: 1px solid #f1f5f9; }
.cc-row:last-child { border-bottom: none; }
.cc-lbl { font-size: 12px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; }
.cc-val { font-size: 14px; font-weight: 600; color: #1e293b; }
.cc-dlg-foot { display: flex; justify-content: flex-end; padding: 0 20px 18px; }
</style>
